import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomRadio from '../../forms/inputs/CustomRadio';
import Anchor from '../../Anchor';
import StripeForm from '../../forms/inputs/StripeForm';
import BancontactForm from '../../forms/inputs/BancontactForm';
import translate from '../../translate';
import Notifications from '../../../services/notifications';
import {
  compose,
  FREQUENCIES_TYPES,
  PAYMENT_METHOD,
} from '../../../services/utilityFunctions';
import withCSRF from '../../withCSRF';
import StripeCardProp from '../../../sections/profile/propTypes/StripeCardProp';
import TenantProp from '../../propTypes/tenantProp';
import VatTypesProp from '../../forms/Address/VatTypesProp';
import PaymentByCompanyInfo from './PaymentByCompanyInfo';
import AddressProp from '../../propTypes/addressProp';
import {
  handleAddCreditCard,
  handleRemoveCreditCard,
} from '../../../services/checkoutHelper';
import { PAYPAL_SUPPORTED_CURRENCIES } from '../../../services/paypalHelper';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';

const PaymentMethods = ({
  t,
  _csrf,
  cards,
  selectedCreditCard,
  accountHolderName,
  tabActive,
  updatePaymentMethod,
  tenant,
  vatTypes,
  companyBilling,
  handleIsLoading,
  isAcademy,
  isSubscription,
  showSaveCard,
  onRemoveCard,
  hidePaypal,
}) => {
  const { loggedUser } = useContext(LoggedUserContext);
  const { currency: userCurrency } = loggedUser;
  const vatTypesList = useMemo(
    () =>
      vatTypes.map(c => ({
        id: c.taxCode,
        text:
          c.taxType === 'eu_vat'
            ? `${c.flagEmoji} ${c.countryName} VAT number`
            : `${c.flagEmoji} ${c.name}`,
      })),
    [vatTypes],
  );

  const { settings = {} } = tenant;
  const { paymentMethods = [] } = settings;
  const [addingNewCard, setAddingNewCard] = useState(false);
  const [attachToUser, setAttachToUser] = useState(true);

  /** HANDLE FUNCTIONS */
  const handleAttachToUser = () => setAttachToUser(prevState => !prevState);

  // Callback to parent component
  const handleChangePaymentMethod = (type, payload) => {
    updatePaymentMethod(type, payload);
  };

  // Update state to hide or show the card form
  const handleCloseAddCard = () => {
    setAddingNewCard(false);
  };
  const handleOpenAddCard = () => {
    setAddingNewCard(true);
  };

  // Select and unselect card
  const handleSelectCard = creditCard => {
    handleChangePaymentMethod('updatedCardSelected', {
      cardId: creditCard.id === selectedCreditCard ? null : creditCard.id,
    });
  };

  /** API REQUESTS */
  // Handle Card requests add and delete
  const handleFinishAddCard = async result => {
    try {
      handleIsLoading(true);
      const response = await handleAddCreditCard(
        _csrf,
        isAcademy,
        result.token,
        attachToUser,
      );
      handleChangePaymentMethod('updatedUserCards', {
        cards: cards.concat(response),
        cardId: response.id,
      });
      handleCloseAddCard();
      handleIsLoading(false);
      setAttachToUser(true);
    } catch (err) {
      handleCloseAddCard();
      Notifications.showErrorFromRequest(err);
      console.log(err);
      handleIsLoading(false);
    }
  };
  const handleRemoveCard = cardId => async () => {
    handleIsLoading(true);
    try {
      await handleRemoveCreditCard(_csrf, cardId, isAcademy);
      handleChangePaymentMethod('removeCreditCard', {
        cards: cards.filter(c => c.id !== cardId),
      });
      onRemoveCard();
      handleCloseAddCard();
      handleIsLoading(false);
    } catch (err) {
      handleCloseAddCard();
      Notifications.showErrorFromRequest(err);
      handleIsLoading(false);
    }
  };

  const isCreditCardAvailable = paymentMethods.includes(
    PAYMENT_METHOD.CREDIT_CARD,
  );
  const isBancontactAvailable =
    !isSubscription && paymentMethods.includes(PAYMENT_METHOD.BANCONTACT);
  const isBankTransferAvailable =
    !isSubscription && paymentMethods.includes(PAYMENT_METHOD.BANK_TRANSFER);
  const isChequeAvailable =
    !isSubscription && paymentMethods.includes(PAYMENT_METHOD.CHEQUE);
  const isCryptoManualAvailable =
    !isSubscription && paymentMethods.includes(PAYMENT_METHOD.CRYPTO_MANUAL);
  const isPaidByCompanyAvailable =
    !isSubscription && paymentMethods.includes(PAYMENT_METHOD.PAID_BY_COMPANY);

  const isCurrencyAvailableForPaypal =
    userCurrency &&
    PAYPAL_SUPPORTED_CURRENCIES.includes(userCurrency.toUpperCase());
  const isPaypalAvailable =
    paymentMethods.includes(PAYMENT_METHOD.PAYPAL) && !hidePaypal;

  const paymentMethodsEmpty = isSubscription
    ? !isCreditCardAvailable
    : paymentMethods.length === 0;
  const isCompanyBilling =
    companyBilling?.emails?.length > 0 && companyBilling?.address?.address_1;

  return (
    <div className="payment-methods-checkout">
      <h4>{t('checkout.payment.header')}</h4>
      <div className="subtitle">{t('shop.choose.payment')}</div>
      {paymentMethodsEmpty && <h6>{t('paymentMethods.none.configured')}</h6>}
      <ul id="paymentList" className="nav nav-pills nav-fill" role="tablist">
        {isCreditCardAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method credit-card nav-link show ${
                tabActive === PAYMENT_METHOD.CREDIT_CARD ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.CREDIT_CARD,
                })
              }
            >
              <img id="creditCardImg" src="/images/shop/card.svg" alt="" />
              <span>{t('card')}</span>
            </button>
          </li>
        )}
        {isPaypalAvailable && !isCompanyBilling && (
          <li className="nav-item paypal-tab-container">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.PAYPAL ? 'active' : ''
              }`}
              disabled={!isCurrencyAvailableForPaypal}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.PAYPAL,
                })
              }
            >
              <img src="/images/shop/paypal.svg" alt="" />
              <span>{t('shop.paypal')}</span>
            </button>
            {!isCurrencyAvailableForPaypal ? (
              <span className="tooltip-text">
                {t('tooltip.paypal.currency')}
              </span>
            ) : null}
          </li>
        )}
        {isBancontactAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.BANCONTACT ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.BANCONTACT,
                })
              }
            >
              <img src="/images/shop/bancontact-logo.svg" alt="" />
              <span>Bancontact</span>
            </button>
          </li>
        )}
        {isBankTransferAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.BANK_TRANSFER ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.BANK_TRANSFER,
                })
              }
            >
              <img src="/images/shop/bank-transfer.svg" alt="" />
              <span>{t('bankTransfer')}</span>
            </button>
          </li>
        )}
        {isChequeAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.CHEQUE ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.CHEQUE,
                })
              }
            >
              <img src="/images/shop/cheque.svg" alt="" />
              <span>{t('cheque')}</span>
            </button>
          </li>
        )}
        {isCryptoManualAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.CRYPTO_MANUAL ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.CRYPTO_MANUAL,
                })
              }
            >
              <img src="/images/shop/crypto.svg" alt="" />
              <span>{t('crypto_manual')}</span>
            </button>
          </li>
        )}
        {isPaidByCompanyAvailable && isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.PAID_BY_COMPANY ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.PAID_BY_COMPANY,
                })
              }
            >
              <img src="/images/shop/companyBilling.svg" alt="" />
              <span>{t('paid_by_company')}</span>
            </button>
          </li>
        )}
      </ul>
      <div className="tab-content">
        {isCreditCardAvailable && tabActive === PAYMENT_METHOD.CREDIT_CARD && (
          <div
            className={`tab-pane show ${
              tabActive === PAYMENT_METHOD.CREDIT_CARD ? 'active' : ''
            }`}
          >
            <h3>{`${t('shop.cart.your_cards')}:`}</h3>
            <ul className="card-list">
              {cards.map((creditCard, idx) => (
                <li key={creditCard.id} className="credit-card-holder">
                  <div className="form-group row">
                    <div className="col-lg-9 col-md-9 credit-card-info row">
                      <div className="col-form-label col-lg-6 col-12">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                        <label className="m-checkbox m-checkbox--state-success">
                          <CustomRadio
                            id={creditCard.id}
                            value={creditCard.id}
                            onClick={() => handleSelectCard(creditCard)}
                            checked={creditCard.id === selectedCreditCard}
                          />
                          <strong>{`**** **** **** ${creditCard.last4}`}</strong>
                          <br />
                          {`${t('expires_at')} ${creditCard.exp_month}/${
                            creditCard.exp_year
                          }`}
                          <span className="span-checkbox" />
                        </label>
                      </div>
                      <div className="credit-card-logo col-lg-4 col-6">
                        <img
                          alt={`${creditCard.brand} logo`}
                          src={creditCard.logo}
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-3 remove-credit-card col-6">
                      <div className="profile-actions">
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                        <a
                          role="button"
                          tabIndex={idx}
                          onClick={handleRemoveCard(creditCard.id)}
                          className="btn btn-metal m-btn m-btn--icon
                                    m-btn--icon-only m-btn--custom m-btn--pill"
                        >
                          <i className="fa fa-trash-alt" />
                        </a>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {!addingNewCard ? (
              <div className="add-new-btn">
                <Anchor
                  onClick={handleOpenAddCard}
                  className="btn btn-outline-metal m-btn m-btn--icon m-btn--icon-only
                             m-btn--custom m-btn--outline-2x m-btn--pill m-btn--air"
                >
                  <i className="la la-plus" />
                  <span>{t('add_card_payment')}</span>
                </Anchor>
              </div>
            ) : (
              <div className="add-new-address">
                <StripeForm
                  t={t}
                  onFinish={handleFinishAddCard}
                  onCancel={handleCloseAddCard}
                  isCheckout
                  attachToUser={attachToUser}
                  onChangeAttachToUser={handleAttachToUser}
                  showSaveCard={showSaveCard}
                  isSubscription={isSubscription}
                />
              </div>
            )}
          </div>
        )}
        {isPaypalAvailable && tabActive === PAYMENT_METHOD.PAYPAL && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.PAYPAL ? 'active' : ''
            }`}
          >
            <h3>{t('shop.checkout.payment.paypal.header')}</h3>
            <span>{t('shop.checkout.payment.bancontact.info')}</span>
            <div className="margin-top-20" />
          </div>
        )}
        {isBancontactAvailable && tabActive === PAYMENT_METHOD.BANCONTACT && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.BANCONTACT ? 'active' : ''
            }`}
          >
            <h3>{t('shop.checkout.payment.bancontact.header')}</h3>
            <span>{t('shop.checkout.payment.bancontact.info')}</span>
            <div className="margin-top-20">
              <BancontactForm
                onAccountHolder={value =>
                  handleChangePaymentMethod('updateHolderName', {
                    accountHolderName: value,
                  })
                }
                accountName={accountHolderName}
              />
            </div>
          </div>
        )}
        {isBankTransferAvailable &&
          tabActive === PAYMENT_METHOD.BANK_TRANSFER && (
            <div
              className={`bank-transfer-payment tab-pane show ${
                tabActive === PAYMENT_METHOD.BANK_TRANSFER ? 'active' : ''
              }`}
            >
              <h3>{t('shop.checkout.payment.bankTransfer.header')}</h3>
              <span>{t('shop.checkout.payment.bankTransfer.info')}</span>
            </div>
          )}
        {isChequeAvailable && tabActive === PAYMENT_METHOD.CHEQUE && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.CHEQUE ? 'active' : ''
            }`}
          >
            <h3>{t('cheque')}</h3>
            <span>{t('shop.checkout.payment.cheque.info')}</span>
          </div>
        )}
        {isCryptoManualAvailable &&
          tabActive === PAYMENT_METHOD.CRYPTO_MANUAL && (
            <div
              className={`bank-transfer-payment tab-pane show ${
                tabActive === PAYMENT_METHOD.CRYPTO_MANUAL ? 'active' : ''
              }`}
            >
              <h3>{t('crypto_manual')}</h3>
              <span>{t('shop.checkout.payment.crypto.info')}.</span>
            </div>
          )}
        {isPaidByCompanyAvailable &&
          tabActive === PAYMENT_METHOD.PAID_BY_COMPANY && (
            <div
              className={`bank-transfer-payment tab-pane show ${
                tabActive === PAYMENT_METHOD.PAID_BY_COMPANY ? 'active' : ''
              }`}
            >
              <div className="company-info-container">
                <PaymentByCompanyInfo
                  companyBilling={companyBilling}
                  vatTypesList={vatTypesList}
                />
              </div>
              <div className="hint-box-paid_by_company">
                <div className="warning-box">
                  <span>{t('payment.byCompany.hint.email')}</span>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

PaymentMethods.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  updatePaymentMethod: PropTypes.func.isRequired,
  tabActive: PropTypes.string.isRequired,
  accountHolderName: PropTypes.string,
  selectedCreditCard: PropTypes.string,
  handleIsLoading: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  companyBilling: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    emails: PropTypes.arrayOf(PropTypes.string),
    taxCode: PropTypes.string,
    taxType: PropTypes.string,
    vatNumber: PropTypes.string,
    address: PropTypes.shape(AddressProp.propType),
  }),
  isAcademy: PropTypes.bool,
  showSaveCard: PropTypes.bool,
  isSubscription: PropTypes.oneOf(FREQUENCIES_TYPES),
  onRemoveCard: PropTypes.func,
  hidePaypal: PropTypes.bool,
};
PaymentMethods.defaultProps = {
  accountHolderName: '',
  selectedCreditCard: '',
  companyBilling: null,
  isAcademy: false,
  isSubscription: null,
  showSaveCard: true,
  onRemoveCard: null,
  hidePaypal: false,
};

export default compose(translate, withCSRF)(PaymentMethods);
